import React, { useMemo, useState } from 'react';
import { IconButton, Stack } from '@mui/material';
import {
  useGetCampaignByIdQuery,
  useSearchCampaignsOnDropdownQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { getDefaultExistingCampaignQueryVariables } from 'modules/analyse/utils/getDefaultExistingCampaignQueryVariables';
import { Visibility } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { Dropdown } from 'common/form';
import { Paths } from 'services/navigation/paths';
import { appendSearchParamsToUrl } from 'utils/fomatting';
import { CAMPAIGN_CREATION_PARAMS } from 'services/campaign/constants';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { AutomationFormItem } from 'modules/analyse/types/AutomationFormItem';
import { useDebounce } from '@uidotdev/usehooks';

type Props = {
  index: number;
  candidateBatchId?: string;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  analysisAutomationItem: AutomationFormItem;
  error?: boolean;
  onCampaignStatusChange?: (status: string | undefined) => void;
};

export const CampaignSearchSelector: React.FC<Props> = ({
  index,
  setFieldValue,
  analysisAutomationItem,
  candidateBatchId,
  error,
  onCampaignStatusChange
}) => {
  const organization = useOrganization();
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 500);

  const { loading, data: searchData } = useSearchCampaignsOnDropdownQuery({
    variables: getDefaultExistingCampaignQueryVariables({
      organizationId: organization.id,
      searchText: debouncedSearchText
    }),
    skip: !organization.id
  });

  const { data: linkedCampaignData } = useGetCampaignByIdQuery({
    variables: {
      campaignId: analysisAutomationItem.action.campaignId || ''
    },
    skip: !analysisAutomationItem.action.campaignId,
    onCompleted: (data) => {
      if (data.getCampaign?.id) {
        onCampaignStatusChange?.(data.getCampaign.status || undefined);
      }
    }
  });

  const campaignOptions = useMemo(() => {
    const options: { label: string; value: string; extraData?: string[] }[] = (
      searchData?.searchCampaigns?.items || []
    ).map((campaign) => {
      const extraData: string[] = [];

      if (campaign?.jobDescriptionParsed?.location) {
        extraData.push(campaign.jobDescriptionParsed.location);
      }

      if (campaign?.user?.name || campaign?.user?.email) {
        extraData.push(campaign.user.name || campaign.user.email);
      }

      return {
        label: campaign?.title || campaign?.id || '',
        value: campaign?.id || '',
        extraData: extraData.length > 0 ? extraData : undefined
      };
    });

    const linkedCampaign = linkedCampaignData?.getCampaign;
    if (
      linkedCampaign?.id &&
      !options.some((opt) => opt.value === linkedCampaign.id)
    ) {
      options.unshift({
        label: linkedCampaign.title || linkedCampaign.id,
        value: linkedCampaign.id
      });
    }

    return options;
  }, [searchData, linkedCampaignData]);

  const navigateToCampaignCreation = () => {
    const url = Paths.CAMPAIGN_CREATE_DRAFT;

    if (candidateBatchId) {
      window.open(
        appendSearchParamsToUrl(url, {
          candidateBatchId,
          from: CAMPAIGN_CREATION_PARAMS.automation
        }),
        '_blank'
      );
      return;
    }

    window.open(
      appendSearchParamsToUrl(url, {
        from: CAMPAIGN_CREATION_PARAMS.automation
      }),
      '_blank'
    );
  };

  const navigateToCampaign = () => {
    window.open(
      `${Paths.OUTBOUND_CAMPAIGN}/${analysisAutomationItem.action.campaignId}`
    );
  };

  return (
    <Stack direction="column" spacing={1}>
      <Stack direction="row" alignItems="flex-end" gap={1}>
        <Dropdown
          name="campaignId"
          field={{
            options: campaignOptions,
            isSearchable: true,
            searchPlaceholder: 'Search campaigns',
            isLoading: loading,
            buttonText: 'Choose campaign',
            onSearchChange: setSearchText,
            stickyButtonEndComponent: {
              text: 'New Campaign',
              startIcon: <AddIcon />,
              onClick: navigateToCampaignCreation
            }
          }}
          error={error}
          value={analysisAutomationItem.action.campaignId || ''}
          onChange={(value) => {
            setFieldValue(`[${index}].action.campaignId`, value);
          }}
        />
        {analysisAutomationItem.action.campaignId && (
          <IconButton
            onClick={navigateToCampaign}
            color="primary"
            sx={{ mb: '13px' }}
          >
            <Visibility />
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
};
